// Database Schema & Collection Helpers
// All Firestore collections, document structures, and CRUD operations

import {
  collection, doc, addDoc, updateDoc, deleteDoc,
  getDocs, getDoc, query, where, onSnapshot,
  serverTimestamp, orderBy, limit, setDoc
} from 'firebase/firestore';
import { db } from './config';

// ─────────────────────────────────────────────
// COLLECTION NAMES
// ─────────────────────────────────────────────
export const COLLECTIONS = {
  USERS:    'users',     // admin + drivers (role field)
  CUSTOMERS:'customers', // customer profiles
  RIDES:    'rides',     // ride requests + assignments
  LOCATIONS:'locations', // live driver GPS positions
};

// ─────────────────────────────────────────────
// DOCUMENT SCHEMAS (for reference)
// ─────────────────────────────────────────────

/*
users/{uid}
{
  uid: string,
  name: string,
  email: string,
  phone: string,
  role: 'admin' | 'driver',
  vehicleNumber: string,    // drivers only
  vehicleType: string,      // drivers only
  isActive: boolean,
  fcmToken: string,         // push notification token
  createdAt: timestamp,
  updatedAt: timestamp
}

customers/{uid}
{
  uid: string,
  name: string,
  email: string,
  phone: string,
  homeAddress: string,
  homeLatitude: number,
  homeLongitude: number,
  fcmToken: string,
  createdAt: timestamp,
  updatedAt: timestamp
}

rides/{id}
{
  id: string,
  customerId: string,
  customerName: string,
  customerPhone: string,
  driverId: string | null,
  driverName: string | null,
  pickupAddress: string,
  pickupLatitude: number,
  pickupLongitude: number,
  pickupDateTime: timestamp,
  stops: [
    { address: string, latitude: number, longitude: number, completed: boolean, order: number }
  ],
  status: 'pending' | 'assigned' | 'accepted' | 'in_progress' | 'completed' | 'cancelled',
  adminNotes: string,
  createdAt: timestamp,
  updatedAt: timestamp
}

locations/{driverId}
{
  driverId: string,
  driverName: string,
  latitude: number,
  longitude: number,
  isOnline: boolean,
  updatedAt: timestamp
}
*/

// ─────────────────────────────────────────────
// DRIVERS
// ─────────────────────────────────────────────
export const driversRef = () => query(
  collection(db, COLLECTIONS.USERS),
  where('role', '==', 'driver')
);

export const getDrivers = async () => {
  const snap = await getDocs(driversRef());
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const addDriver = async (data) => {
  return await addDoc(collection(db, COLLECTIONS.USERS), {
    ...data,
    role: 'driver',
    isActive: true,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
};

export const updateDriver = async (id, data) => {
  await updateDoc(doc(db, COLLECTIONS.USERS, id), {
    ...data,
    updatedAt: serverTimestamp()
  });
};

export const deleteDriver = async (id) => {
  await deleteDoc(doc(db, COLLECTIONS.USERS, id));
};

// ─────────────────────────────────────────────
// CUSTOMERS
// ─────────────────────────────────────────────
export const getCustomers = async () => {
  const snap = await getDocs(collection(db, COLLECTIONS.CUSTOMERS));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const getCustomer = async (id) => {
  const snap = await getDoc(doc(db, COLLECTIONS.CUSTOMERS, id));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

export const addCustomer = async (uid, data) => {
  await setDoc(doc(db, COLLECTIONS.CUSTOMERS, uid), {
    ...data,
    uid,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
};

export const updateCustomer = async (id, data) => {
  await updateDoc(doc(db, COLLECTIONS.CUSTOMERS, id), {
    ...data,
    updatedAt: serverTimestamp()
  });
};

// ─────────────────────────────────────────────
// RIDES
// ─────────────────────────────────────────────
export const getRides = async (statusFilter = null) => {
  let q = collection(db, COLLECTIONS.RIDES);
  if (statusFilter) {
    q = query(q, where('status', '==', statusFilter), orderBy('createdAt', 'desc'));
  } else {
    q = query(q, orderBy('createdAt', 'desc'));
  }
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

export const createRide = async (data) => {
  return await addDoc(collection(db, COLLECTIONS.RIDES), {
    ...data,
    driverId: null,
    driverName: null,
    status: 'pending',
    stops: data.stops || [],
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
};

export const assignDriver = async (rideId, driverId, driverName) => {
  await updateDoc(doc(db, COLLECTIONS.RIDES, rideId), {
    driverId,
    driverName,
    status: 'assigned',
    updatedAt: serverTimestamp()
  });
};

export const updateRideStatus = async (rideId, status) => {
  await updateDoc(doc(db, COLLECTIONS.RIDES, rideId), {
    status,
    updatedAt: serverTimestamp()
  });
};

export const addStopToRide = async (rideId, stop) => {
  const rideDoc = await getDoc(doc(db, COLLECTIONS.RIDES, rideId));
  const existing = rideDoc.data().stops || [];
  await updateDoc(doc(db, COLLECTIONS.RIDES, rideId), {
    stops: [...existing, { ...stop, completed: false, order: existing.length }],
    updatedAt: serverTimestamp()
  });
};

export const markStopCompleted = async (rideId, stopIndex) => {
  const rideDoc = await getDoc(doc(db, COLLECTIONS.RIDES, rideId));
  const stops = [...(rideDoc.data().stops || [])];
  stops[stopIndex].completed = true;
  await updateDoc(doc(db, COLLECTIONS.RIDES, rideId), {
    stops,
    updatedAt: serverTimestamp()
  });
};

// ─────────────────────────────────────────────
// LIVE LISTENERS (realtime)
// ─────────────────────────────────────────────
export const listenToRides = (callback, statusFilter = null) => {
  let q = collection(db, COLLECTIONS.RIDES);
  if (statusFilter) {
    q = query(q, where('status', '==', statusFilter), orderBy('createdAt', 'desc'));
  } else {
    q = query(q, orderBy('createdAt', 'desc'));
  }
  return onSnapshot(q, (snap) => {
    const rides = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    callback(rides);
  });
};

export const listenToDriverLocations = (callback) => {
  return onSnapshot(collection(db, COLLECTIONS.LOCATIONS), (snap) => {
    const locations = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    callback(locations);
  });
};

export const listenToDriverRides = (driverId, callback) => {
  const q = query(
    collection(db, COLLECTIONS.RIDES),
    where('driverId', '==', driverId),
    where('status', 'in', ['assigned', 'accepted', 'in_progress'])
  );
  return onSnapshot(q, (snap) => {
    const rides = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    callback(rides);
  });
};

export const listenToCustomerRides = (customerId, callback) => {
  const q = query(
    collection(db, COLLECTIONS.RIDES),
    where('customerId', '==', customerId),
    orderBy('createdAt', 'desc')
  );
  return onSnapshot(q, (snap) => {
    const rides = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    callback(rides);
  });
};

// ─────────────────────────────────────────────
// DRIVER LOCATION
// ─────────────────────────────────────────────
export const updateDriverLocation = async (driverId, driverName, lat, lng) => {
  await setDoc(doc(db, COLLECTIONS.LOCATIONS, driverId), {
    driverId,
    driverName,
    latitude: lat,
    longitude: lng,
    isOnline: true,
    updatedAt: serverTimestamp()
  });
};

export const setDriverOffline = async (driverId) => {
  await updateDoc(doc(db, COLLECTIONS.LOCATIONS, driverId), {
    isOnline: false,
    updatedAt: serverTimestamp()
  });
};
